import { useMemo } from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconRefresh } from "@tabler/icons-react";
import { ExtractionStepState } from "../../types";
import { getFailedExtractionStepKeys } from "../../utils/extractionStatusPolling";
import { StepStatusIcon } from "./StepStatusIcon";

interface ExtractionProgressProps {
  isExtracting: boolean;
  error?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  onRetryStep?: (stepKey: string) => void;
  rerunningSteps?: Set<string>;
  progress?: number;
  steps?: Record<string, ExtractionStepState>;
  currentPhase?: 1 | 2 | 3;
}

interface StepEntry {
  key: string;
  label: string;
  phase: 1 | 2 | 3;
  fields: string[];
}

const STEP_CONFIG: StepEntry[] = [
  {
    key: "A",
    label: "Patient Demographics",
    phase: 1,
    fields: ["Name", "Date of birth", "Address", "Phone", "Email", "MRN"],
  },
  {
    key: "C",
    label: "Insurance",
    phase: 1,
    fields: [
      "Primary insurer",
      "Plan type",
      "Policy #",
      "Group #",
      "Secondary insurer",
    ],
  },
  {
    key: "F",
    label: "Clinical Details",
    phase: 1,
    fields: [
      "Collection date",
      "Clinical history",
      "Indications",
      "Impressions",
      "Rule-outs",
    ],
  },
  {
    key: "E",
    label: "Practice & Physician",
    phase: 1,
    fields: ["Practice", "Location", "Physician", "Procedure"],
  },
  {
    key: "B",
    label: "Materials Received",
    phase: 1,
    fields: ["Jars / tissue", "Slides / blocks", "External case #"],
  },
  {
    key: "K",
    label: "Document Signals",
    phase: 1,
    fields: [
      "Rush flag",
      "High priority flag",
      "Cytology flag",
      "Consultation flag",
      "TC-only flag",
    ],
  },
  {
    key: "D",
    label: "Consultation Details",
    phase: 1,
    fields: [
      "Reason for consult",
      "Original diagnosis",
      "Requesting pathologist",
    ],
  },
  {
    key: "H",
    label: "Originating Facility",
    phase: 1,
    fields: ["Referring facility", "Physician", "Address"],
  },
  {
    key: "I",
    label: "Shipping & Pathologist",
    phase: 1,
    fields: ["External pathologist", "Shipping address"],
  },
  {
    key: "J",
    label: "Specimens",
    phase: 1,
    fields: [
      "Source",
      "Subsite",
      "Findings",
      "Collection method",
      "Gross description",
    ],
  },
  {
    key: "L",
    label: "Lab Annotations",
    phase: 1,
    fields: ["Stain codes", "Gross codes", "Block identifiers", "Dimensions"],
  },
];

const PHASE1_STEPS = STEP_CONFIG.filter((s) => s.phase === 1);
const PHASE3_STEPS = STEP_CONFIG.filter((s) => s.phase === 3);

export default function ExtractionProgress({
  isExtracting,
  error,
  onRetry,
  retryLabel = "Retry Extraction",
  onRetryStep,
  rerunningSteps,
  progress,
  steps,
  currentPhase,
}: ExtractionProgressProps) {
  const hasStepData = useMemo(
    () => steps != null && Object.keys(steps).length > 0,
    [steps]
  );
  const phase2Active = useMemo(() => (currentPhase ?? 0) >= 2, [currentPhase]);
  const phase3Active = useMemo(() => (currentPhase ?? 0) >= 3, [currentPhase]);
  const failedStepCount = useMemo(
    () => getFailedExtractionStepKeys(steps).length,
    [steps]
  );
  const hasFailedSteps = failedStepCount > 0;

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Extraction Failed"
        color="red"
        role="alert"
      >
        <Stack gap="sm">
          <Text size="sm">{error}</Text>
          {onRetry && (
            <Button
              leftSection={<IconRefresh size={16} />}
              size="sm"
              variant="light"
              onClick={onRetry}
            >
              {retryLabel}
            </Button>
          )}
        </Stack>
      </Alert>
    );
  }

  // Show progress while extracting or if there are completed steps to display
  if (!isExtracting && !hasStepData) return null;

  if (!hasStepData) {
    return (
      <Stack gap="xs" align="center" py="xs">
        <Loader size="md" type="dots" />
        <Text size="sm" fw={500}>
          Extracting data from documents…
        </Text>
        <Text size="xs" c="dimmed">
          AI is analyzing your documents
        </Text>
        {progress !== undefined && (
          <Progress value={progress} size="sm" w="100%" animated />
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      {progress !== undefined && (
        <Group justify="space-between" mb={4}>
          <Text size="xs" c="dimmed">
            {isExtracting ? `${progress}%` : "100%"}
          </Text>
          {!isExtracting && hasStepData && (
            <Text size="xs" c={hasFailedSteps ? "orange.7" : "green"} fw={500}>
              {hasFailedSteps ? "Incomplete" : "Complete"}
            </Text>
          )}
        </Group>
      )}

      {!isExtracting && hasFailedSteps && (
        <Alert
          aria-label="Extraction incomplete"
          color="orange"
          icon={<IconAlertCircle size={16} />}
          role="alert"
          title="Extraction incomplete"
        >
          Some sections could not be extracted. Use the retry button beside each
          failed section, or enter those details manually.
        </Alert>
      )}

      {PHASE1_STEPS.map((entry) => (
        <Tooltip
          key={entry.key}
          label={
            <Stack gap={2}>
              {entry.fields.map((f) => (
                <Text key={f} size="xs">
                  {f}
                </Text>
              ))}
            </Stack>
          }
          position="right"
          withinPortal
          multiline
          w={160}
        >
          <Group gap="xs" wrap="nowrap" style={{ cursor: "default" }}>
            <Box
              w={16}
              style={{ flexShrink: 0, display: "flex", alignItems: "center" }}
            >
              <StepStatusIcon status={steps?.[entry.key]?.status} />
            </Box>
            <Text
              size="xs"
              c={
                steps?.[entry.key]?.status === "completed"
                  ? "green.7"
                  : "inherit"
              }
              style={{ flex: 1 }}
            >
              {entry.label}
            </Text>
            {(["completed", "failed"].includes(
              steps?.[entry.key]?.status ?? ""
            ) ||
              rerunningSteps?.has(entry.key)) &&
              onRetryStep && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color={
                    steps?.[entry.key]?.status === "failed" ? "red" : "gray"
                  }
                  loading={rerunningSteps?.has(entry.key)}
                  onClick={() => onRetryStep(entry.key)}
                  style={{ flexShrink: 0 }}
                  aria-label="Re-run step"
                >
                  <IconRefresh size={10} />
                </ActionIcon>
              )}
          </Group>
        </Tooltip>
      ))}

      <Divider my={4} />

      <Group gap="xs" wrap="nowrap">
        <Box
          w={16}
          style={{ flexShrink: 0, display: "flex", alignItems: "center" }}
        >
          {phase3Active ? (
            <ThemeIcon size={16} radius="xl" color="green" variant="filled">
              <IconCheck size={10} />
            </ThemeIcon>
          ) : (
            <StepStatusIcon status={phase2Active ? "running" : "pending"} />
          )}
        </Box>
        <Text
          size="xs"
          c={phase3Active ? "green.7" : phase2Active ? "inherit" : "dimmed"}
        >
          Type Resolution
        </Text>
      </Group>

      <Divider my={4} />

      {PHASE3_STEPS.map((entry) => (
        <Tooltip
          key={entry.key}
          label={
            <Stack gap={2}>
              {entry.fields.map((f) => (
                <Text key={f} size="xs">
                  {f}
                </Text>
              ))}
            </Stack>
          }
          position="right"
          withinPortal
          multiline
          w={160}
        >
          <Group gap="xs" wrap="nowrap" style={{ cursor: "default" }}>
            <Box
              w={16}
              style={{ flexShrink: 0, display: "flex", alignItems: "center" }}
            >
              <StepStatusIcon status={steps?.[entry.key]?.status} />
            </Box>
            <Text
              size="xs"
              c={
                !phase3Active
                  ? "dimmed"
                  : steps?.[entry.key]?.status === "completed"
                  ? "green.7"
                  : "inherit"
              }
              style={{ flex: 1 }}
            >
              {entry.label}
            </Text>
            {(["completed", "failed"].includes(
              steps?.[entry.key]?.status ?? ""
            ) ||
              rerunningSteps?.has(entry.key)) &&
              onRetryStep && (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color={
                    steps?.[entry.key]?.status === "failed" ? "red" : "gray"
                  }
                  loading={rerunningSteps?.has(entry.key)}
                  onClick={() => onRetryStep(entry.key)}
                  style={{ flexShrink: 0 }}
                  aria-label="Re-run step"
                >
                  <IconRefresh size={10} />
                </ActionIcon>
              )}
          </Group>
        </Tooltip>
      ))}
    </Stack>
  );
}
